/** Branch Manager — Admin-like UI, data limited to one employeeTeam. */

export const BRANCH_MANAGER_ROLE = "Branch Manager";

export function normalizeRole(role) {
  return String(role || "")
    .toLowerCase()
    .trim();
}

export function isBranchManager(roleOrAuth) {
  const role =
    typeof roleOrAuth === "string"
      ? roleOrAuth
      : roleOrAuth?.userRole ||
        roleOrAuth?.user?.userRole ||
        roleOrAuth?.userAccess?.[0]?.userRole;
  return normalizeRole(role) === "branch manager";
}

/** Elevated ops roles (company-wide or branch-scoped). */
export function isElevatedRole(role) {
  const r = normalizeRole(role);
  return (
    r === "admin" ||
    r === "super admin" ||
    r === "supervisor" ||
    r === "branch manager"
  );
}

/**
 * Team this Branch Manager owns.
 * Prefer users.managedTeam; fallback to linked employee.employeeTeam.
 */
export function getManagedTeam(auth) {
  if (!auth) return null;
  const fromUser =
    auth?.user?.managedTeam ||
    auth?.userAccess?.[0]?.managedTeam ||
    auth?.managedTeam;
  if (fromUser) return String(fromUser).trim();
  const fromEmp = auth?.employee?.employeeTeam;
  return fromEmp ? String(fromEmp).trim() : null;
}

export function filterEmployeesByTeam(employees, team) {
  if (!team) return Array.isArray(employees) ? employees : [];
  const t = String(team).trim().toLowerCase();
  return (employees || []).filter(
    (e) => String(e?.employeeTeam || "").trim().toLowerCase() === t,
  );
}

export function filterClockingsByEmployeeIds(clockings, employeeIds) {
  const ids =
    employeeIds instanceof Set
      ? employeeIds
      : new Set(employeeIds || []);
  if (!ids.size) return [];
  return (clockings || []).filter((c) => ids.has(c?.employeeId));
}

/** Query object for employees list API (Branch Manager). */
export function employeesTeamFilter(team) {
  if (!team) return false;
  // Prefer dedicated qs key (supports combining with status filters server-side).
  return { employeeTeam: team };
}

/** Amplify API options for employees list when caller is Branch Manager. */
export function employeesListApiOptions(auth) {
  if (!isBranchManager(auth)) return {};
  const team = getManagedTeam(auth);
  const filter = employeesTeamFilter(team);
  return filter ? { queryStringParameters: filter } : {};
}

/** Apply Branch Manager team filter to an employees API response. */
export function scopeEmployeesList(employees, auth) {
  if (!isBranchManager(auth)) return employees || [];
  return filterEmployeesByTeam(employees, getManagedTeam(auth));
}
